import { Container, useBreakpointValue } from "@chakra-ui/react";
import Head from "next/head";
import { getSession, signIn } from "next-auth/react";
import { useEffect, useState } from "react";

import { getApiBaseUrl } from "@/config/config";
import { usePostDevFavoritesMutation } from "@/hooks/microservicesApi";
import { cardData } from "@/hooks/useBuscadorHook";
import Section from "@/sections/general/Section";
import { BlogGuia } from "@/sections/home/BlogGuia";
import Ciudades from "@/sections/home/Cities";
import FeaturedProperties from "@/sections/home/FeaturedProperties";
import Footer from "@/sections/home/Footer";
import Hero from "@/sections/home/Hero";
import NewDevelops from "@/sections/home/NewDevelops";
import PlacesCards from "@/sections/home/places";
import SitesCards from "@/sections/home/Sites";
import { poppins } from "@/styles/fonts";
import styles from "@/styles/Home.module.css";

import Header from "../sections/home/Header";

// Función auxiliar para manejar fetch con timeout y errores
const fetchWithTimeout = async (url, options = {}, timeout = 5000) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
        const response = await fetch(url, {
            ...options,
            signal: controller.signal,
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json();
    } catch (error) {
        console.error(`Error fetching ${url}:`, error.message);
        return null;
    } finally {
        clearTimeout(timeoutId);
    }
};

export async function getServerSideProps(context) {
    //Obtener datos de sesión
    const session = await getSession(context);

    const body = {
        company: ["GUIA", "GUIACDMX", "GUIAQRO", "GUIACABOS", "GUIAVALLARTA", "GUIALEON"],
        page: 0,
        limit: 100,
        city: "",
        state: "",
        latitude: "",
        longitude: "",
        online_sale: true,
    };

    const featuredBodyRequest = {
        operation: [],
        type: ["HOUSE", "APARTMENT", "TERRAIN"],
        company: ["GUIA"],
        price: [],
        groundSize: [],
        bedrooms: [],
        bathrooms: [],
        halfBathrooms: [],
        pakingLots: [],
        keywords: [],
        radius: 5000,
        range: 20,
    };

    const baseUrl = getApiBaseUrl();
    const HUBSPOT_API_URL = "https://api.hubapi.com/cms/v3/blogs/posts";
    const HUBSPOT_ACCESS_TOKEN = process.env.HUBSPOT_ACCESS_TOKEN;

    // Configuración de parámetros para HubSpot
    const params = new URLSearchParams();
    params.append("sort", "-createdAt");
    params.append("language__not_null", "");
    params.append("limit", "10");
    params.append("offset", "0");
    params.append("state", "PUBLISHED");
    const urlWithParams = `${HUBSPOT_API_URL}?${params.toString()}`;

    const fetchSearchDevs = fetchWithTimeout(`${baseUrl}/searchDevs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
    });

    const fetchSearchFeatured = fetchWithTimeout(`${baseUrl}/searchFeatured`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(featuredBodyRequest),
    });

    const fetchCitiesData = fetchWithTimeout(`${baseUrl}/cities/property-count`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
    });

    const fetchGuiaBlog = fetchWithTimeout(urlWithParams, {
        method: "GET",
        headers: {
            Authorization: `Bearer ${HUBSPOT_ACCESS_TOKEN}`,
            "Content-Type": "application/json",
            Accept: "application/json",
        },
    });

    //Obtener favoritos, solo si hay sesión
    const fetchFavorites = fetchWithTimeout(`${baseUrl}/user/getfavorites`, {
        method: "POST",
        body: JSON.stringify({ contact_id: session?.contact_id }),
        headers: {
            "Content-Type": "application/json",
            Authorization: session ? `Bearer ${session?.token}` : "",
        },
    });

    try {
        const [searchDevs, searchFeatured, citiesData, guiaBlog, favorites] = await Promise.all([
            fetchSearchDevs,
            fetchSearchFeatured,
            fetchCitiesData,
            fetchGuiaBlog,
            session?.token ? fetchFavorites : Promise.resolve(null),
        ]);

        // 3. Se retornan los resultados (los fallos ya están controlados por fetchWithTimeout)
        return {
            props: {
                searchDevs: searchDevs || [], // Si es null, se asigna []
                searchFeatured: searchFeatured || [],
                citiesData: citiesData || [],
                guiaBlog: guiaBlog?.results || [],
                cardData,
                favorites: favorites || [],
                HUBSPOT_ACCESS_TOKEN: HUBSPOT_ACCESS_TOKEN || null,
                userSession: session || null,
            },
        };
    } catch (error) {
        console.error("Error general en getServerSideProps:", error.message);
        return {
            props: {
                searchDevs: [],
                searchFeatured: [],
                citiesData: [],
                guiaBlog: [],
                cardData,
                favorites: [],
                HUBSPOT_ACCESS_TOKEN: HUBSPOT_ACCESS_TOKEN || null,
                userSession: session || null,
            },
        };
    }
}

export default function Home({
    searchDevs,
    searchFeatured,
    cardData,
    citiesData,
    guiaBlog,
    favorites,
    userSession,
}) {
    const isGreaterThanMd = useBreakpointValue({ base: false, md: true });

    //Inicializar el endpoint para guardado de Favoritos
    const [postDevFavorites] = usePostDevFavoritesMutation();

    //Inicializar los favoritos, declarar Set para actualizar la lista con los datos actualizados.
    const [userFavorites, setUserFavorites] = useState(favorites || []);

    useEffect(() => {
        setUserFavorites(favorites);
    }, [favorites]);

    useEffect(() => {
        const feedbackContainer = document.getElementById("_hj_feedback_container");
        if (feedbackContainer) {
            feedbackContainer.style.display = "none";
        }
    }, []);

    //Función para añadir favoritos a la base de datos
    const savePropsFavorites = async (propertyId, devId) => {
        try {
            const response = await postDevFavorites({
                body: {
                    contact_id: userSession?.contact_id,
                    property_id: propertyId || null,
                    development_id: devId || null,
                    changed: new Date().toISOString(),
                },
                token: userSession?.token,
            }).unwrap();

            setUserFavorites(response?.favorites);
        } catch (error) {
            console.error("Error al guardar favorito:", error);
            if (error?.status === 401) {
                getSession().then((data) => {
                    if (data?.error === "RefreshAccessTokenError") {
                        console.warn("Token refresh failed. Redirecting to login...");
                        return signIn("keycloak");
                    }
                });
            }
        }
    };

    return (
        <>
            <Head>
                <title>Grupo Guía | Excelencia Inmobiliaria | Inicio</title>
                <link rel="canonical" href="https://grupoguia.mx/" />
                <link rel="alternate" href="https://grupoguia.mx/" hrefLang="es-mx" />
                <link rel="preconnect" href="https://fonts.googleapis.com" />
                <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="true" />
            </Head>
            <style jsx global>
                {`
                    html {
                        font-family: ${poppins.style.fontFamily};
                    }
                `}
            </style>
            <main className={styles.main}>
                <Header />
                <Container
                    maxW="full"
                    {...(isGreaterThanMd && { paddingInline: "0" })}
                    padding="0px"
                    bg="white"
                >
                    <Section
                        {...(isGreaterThanMd && { full: "full" })}
                        mb={isGreaterThanMd ? 28 : 0}
                    >
                        <Hero />
                    </Section>
                </Container>
                <NewDevelops data={searchDevs} />
                <PlacesCards data={cardData} isGreaterThanMd={isGreaterThanMd} />
                <FeaturedProperties
                    data={searchFeatured}
                    session={userSession}
                    savePropsFavorites={savePropsFavorites}
                    userFavorites={userFavorites}
                />
                <Ciudades data={citiesData} />
                <SitesCards />
                <BlogGuia data={guiaBlog} />
                <Footer />
            </main>
        </>
    );
}
